import React, { useState } from "react";
import {
  Modal,
  Stack,
  Group,
  Text,
  TextInput,
  FileInput,
  Button,
  Select,
  ActionIcon,
} from "@mantine/core";
import { IconPlus, IconPencil, IconX } from "@tabler/icons-react";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { esignatureService } from "../../services/esignature";
import { handleGenericError } from "../../utils/errorHandler";

const DEFAULT_COLORS = [
  "#4A90E2",
  "#E24A4A",
  "#4AE290",
  "#E2904A",
  "#904AE2",
  "#E2E24A",
  "#4AE2E2",
  "#E24A90",
];

const ROLE_TYPE_OPTIONS = [
  { value: "signer", label: "Needs to Sign" },
  { value: "approver", label: "Approver" },
  { value: "cc", label: "Receives a Copy (CC)" },
];

const formatRoleType = roleType => {
  if (roleType === "cc") return "CC";
  if (roleType === "signer") return "Signer";
  if (roleType === "approver") return "Approver";
  return roleType.charAt(0).toUpperCase() + roleType.slice(1);
};

// Guarantor and Direct Seller come back as default recipients on every uploaded
// template and can't be edited or removed.
const DEFAULT_RECIPIENT_NAMES = ["guarantor", "direct seller"];
const isDefaultRecipient = role => {
  if (role?.is_default || role?.is_locked) return true;
  const name = (role?.full_name || "").trim().toLowerCase();
  return DEFAULT_RECIPIENT_NAMES.includes(name);
};

// Sub-modal for adding / editing a single recipient role.
export function RoleFormModal({
  opened,
  onClose,
  onSubmit,
  existingRoles,
  role,
}) {
  const isEditing = !!role;
  const [name, setName] = useState("");
  const [type, setType] = useState(null);
  const [color, setColor] = useState(DEFAULT_COLORS[0]);

  // Reset the form whenever the modal opens.
  React.useEffect(() => {
    if (opened) {
      setName(role?.full_name || "");
      setType(role?.role || null);
      setColor(
        role?.color ||
          DEFAULT_COLORS[existingRoles.length % DEFAULT_COLORS.length]
      );
    }
  }, [opened, role, existingRoles.length]);

  const handleSubmit = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    if (!type) return;
    onSubmit({ name: trimmed, type, color });
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={isEditing ? "Edit Role" : "Add Role"}
      size="md"
      zIndex={1100}
    >
      <Stack gap="md">
        <TextInput
          label="Role Name"
          placeholder="e.g., Direct Seller, Guarantor"
          value={name}
          onChange={e => setName(e.target.value)}
          required
          data-autofocus
        />
        <Select
          label="Role Type"
          placeholder="Select role type"
          data={ROLE_TYPE_OPTIONS}
          value={type}
          onChange={setType}
          allowDeselect={false}
        />
        <div>
          <Text size="sm" fw={500} mb={6}>
            Color
          </Text>
          <Group gap="xs">
            {DEFAULT_COLORS.map(c => (
              <div
                key={c}
                onClick={() => setColor(c)}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  backgroundColor: c,
                  cursor: "pointer",
                  border:
                    color === c ? "3px solid #333" : "3px solid transparent",
                }}
              />
            ))}
          </Group>
        </div>
        <Group justify="flex-end" mt="sm">
          <Button variant="subtle" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit}>{isEditing ? "Save" : "Add"}</Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export function RecipientsStep({ roles, onAdd, onEdit, onDelete }) {
  const sortedRoles = [...roles].sort(
    (a, b) => (a.signing_order || 0) - (b.signing_order || 0)
  );

  return (
    <Stack gap="sm">
      <Text size="sm" fw={700} c="dimmed" style={{ letterSpacing: "0.5px" }}>
        RECIPIENTS
      </Text>

      {sortedRoles.length === 0 ? (
        <Text size="sm" c="dimmed" py="md">
          No recipients added yet.
        </Text>
      ) : (
        <Stack gap="xs">
          {sortedRoles.map(role => {
            const locked = isDefaultRecipient(role);
            return (
              <Group
                key={role.id}
                wrap="nowrap"
                justify="space-between"
                p="sm"
                style={{
                  border: "2px solid #eee",
                  borderRadius: 8,
                }}
              >
                <Group gap="sm" wrap="nowrap">
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      backgroundColor: role.color,
                      color: "white",
                      fontWeight: 700,
                      fontSize: 16,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {role.signing_order}
                  </div>
                  <Stack gap={0}>
                    <Text fw={600} size="sm">
                      {role.full_name}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {formatRoleType(role.role)}
                    </Text>
                  </Stack>
                </Group>
                {!locked && (
                  <Group gap={4} wrap="nowrap">
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      onClick={() => onEdit(role)}
                      aria-label="Edit role"
                    >
                      <IconPencil size={16} />
                    </ActionIcon>
                    {roles.length > 1 && (
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        onClick={() => onDelete(role)}
                        aria-label="Delete role"
                      >
                        <IconX size={16} />
                      </ActionIcon>
                    )}
                  </Group>
                )}
              </Group>
            );
          })}
        </Stack>
      )}

      <Button
        fullWidth
        color="orange"
        leftSection={<IconPlus size={16} />}
        onClick={onAdd}
      >
        Add Role
      </Button>
    </Stack>
  );
}

function UploadAgreementModal({ opened, onClose, onUploaded, sellingYearId }) {
  const { getVerifiedJWT } = useAuth();
  const { showError, showSuccess } = useToast();

  const [step, setStep] = useState(1);
  const [name, setName] = useState("");
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [templateId, setTemplateId] = useState(null);
  const [roles, setRoles] = useState([]);
  const [roleModalOpened, setRoleModalOpened] = useState(false);
  const [editingRole, setEditingRole] = useState(null);

  const resetAndClose = () => {
    setStep(1);
    setName("");
    setFile(null);
    setTemplateId(null);
    setRoles([]);
    setEditingRole(null);
    setRoleModalOpened(false);
    onClose();
  };

  const saveRoles = async updatedRoles => {
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt || !templateId) return;
      await esignatureService.updateTemplate(
        templateId,
        { roles_metadata: updatedRoles },
        jwt
      );
    } catch (err) {
      handleGenericError(err, "Failed to save recipients");
    }
  };

  const handleUpload = async () => {
    if (!name.trim()) {
      showError("Please enter a name");
      return;
    }
    if (!file) {
      showError("Please select a file");
      return;
    }

    try {
      setSubmitting(true);
      const jwt = await getVerifiedJWT();
      if (!jwt) {
        throw new Error("Authentication failed");
      }

      const formData = new FormData();
      formData.append("file", file);
      formData.append("name", name.trim());
      if (sellingYearId != null) {
        formData.append("selling_year_id", sellingYearId);
      }

      const result = await esignatureService.createTemplate(formData, jwt);
      const created = result?.data || result || {};
      setTemplateId(created.id);
      setRoles(
        Array.isArray(created.roles_metadata) ? created.roles_metadata : []
      );
      showSuccess("Agreement uploaded successfully!");
      if (onUploaded) onUploaded();
      setStep(2);
    } catch (err) {
      handleGenericError(err, "Failed to upload agreement");
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddRole = () => {
    setEditingRole(null);
    setRoleModalOpened(true);
  };

  const handleEditRole = role => {
    setEditingRole(role);
    setRoleModalOpened(true);
  };

  const handleDeleteRole = role => {
    const updated = roles
      .filter(r => r.id !== role.id)
      .map((r, idx) => ({ ...r, signing_order: idx + 1 }));
    setRoles(updated);
    saveRoles(updated);
  };

  const handleRoleSubmit = ({ name: roleName, type, color }) => {
    let updated;
    if (editingRole) {
      updated = roles.map(r =>
        r.id === editingRole.id
          ? { ...r, full_name: roleName, role: type, color }
          : r
      );
    } else {
      updated = [
        ...roles,
        {
          id: `role-${roles.length + 1}-${name}`,
          role: type,
          full_name: roleName,
          email: `${roleName.toLowerCase().replace(/\s+/g, "")}@example.com`,
          color,
          signing_order: roles.length + 1,
        },
      ];
    }
    setRoles(updated);
    setRoleModalOpened(false);
    setEditingRole(null);
    saveRoles(updated);
  };

  return (
    <>
      <Modal
        opened={opened}
        onClose={resetAndClose}
        title={step === 1 ? "Upload Agreement" : "Recipients"}
        size="lg"
      >
        {step === 1 ? (
          <Stack gap="md">
            <TextInput
              label="Name"
              placeholder="e.g., Rep Agreement"
              value={name}
              onChange={e => setName(e.target.value)}
              required
              data-autofocus
            />
            <FileInput
              label="Document"
              placeholder="Upload a PDF"
              accept="application/pdf"
              value={file}
              onChange={setFile}
              clearable
              required
            />
            <Group justify="flex-end" mt="md">
              <Button
                variant="subtle"
                onClick={resetAndClose}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button onClick={handleUpload} loading={submitting}>
                Upload
              </Button>
            </Group>
          </Stack>
        ) : (
          <Stack gap="lg">
            <RecipientsStep
              roles={roles}
              onAdd={handleAddRole}
              onEdit={handleEditRole}
              onDelete={handleDeleteRole}
            />
            <Group justify="flex-end">
              <Button onClick={resetAndClose}>Done</Button>
            </Group>
          </Stack>
        )}
      </Modal>

      <RoleFormModal
        opened={roleModalOpened}
        onClose={() => {
          setRoleModalOpened(false);
          setEditingRole(null);
        }}
        onSubmit={handleRoleSubmit}
        existingRoles={roles}
        role={editingRole}
      />
    </>
  );
}

export function EditAgreementModal({ opened, onClose, onUpdated, agreement }) {
  const { getVerifiedJWT } = useAuth();
  const { showError, showSuccess } = useToast();

  const [name, setName] = useState("");
  const [roles, setRoles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [roleModalOpened, setRoleModalOpened] = useState(false);
  const [editingRole, setEditingRole] = useState(null);

  // Load the agreement's current values whenever the modal opens.
  React.useEffect(() => {
    if (opened && agreement) {
      setName(agreement.name || "");
      setRoles(
        Array.isArray(agreement.roles_metadata) ? agreement.roles_metadata : []
      );
    }
  }, [opened, agreement]);

  const handleAddRole = () => {
    setEditingRole(null);
    setRoleModalOpened(true);
  };

  const handleEditRole = role => {
    setEditingRole(role);
    setRoleModalOpened(true);
  };

  const handleDeleteRole = role => {
    setRoles(prev =>
      prev
        .filter(r => r.id !== role.id)
        .map((r, idx) => ({ ...r, signing_order: idx + 1 }))
    );
  };

  const handleRoleSubmit = ({ name: roleName, type, color }) => {
    if (editingRole) {
      setRoles(prev =>
        prev.map(r =>
          r.id === editingRole.id
            ? { ...r, full_name: roleName, role: type, color }
            : r
        )
      );
    } else {
      setRoles(prev => [
        ...prev,
        {
          id: `role-${prev.length + 1}-${roleName}`,
          role: type,
          full_name: roleName,
          email: `${roleName.toLowerCase().replace(/\s+/g, "")}@example.com`,
          color,
          signing_order: prev.length + 1,
        },
      ]);
    }
    setRoleModalOpened(false);
    setEditingRole(null);
  };

  const handleSave = async () => {
    if (!name.trim()) {
      showError("Please enter a name");
      return;
    }

    try {
      setSubmitting(true);
      const jwt = await getVerifiedJWT();
      if (!jwt) {
        throw new Error("Authentication failed");
      }

      await esignatureService.updateTemplate(
        agreement.id,
        { name: name.trim(), roles_metadata: roles },
        jwt
      );

      showSuccess("Agreement updated successfully!");
      if (onUpdated) onUpdated();
      onClose();
    } catch (err) {
      handleGenericError(err, "Failed to update agreement");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Modal opened={opened} onClose={onClose} title="Edit Agreement" size="lg">
        <Stack gap="lg">
          <TextInput
            label="Name"
            placeholder="e.g., Rep Agreement"
            value={name}
            onChange={e => setName(e.target.value)}
            required
            data-autofocus
          />

          <RecipientsStep
            roles={roles}
            onAdd={handleAddRole}
            onEdit={handleEditRole}
            onDelete={handleDeleteRole}
          />

          <Group justify="flex-end">
            <Button variant="subtle" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button onClick={handleSave} loading={submitting}>
              Save
            </Button>
          </Group>
        </Stack>
      </Modal>

      <RoleFormModal
        opened={roleModalOpened}
        onClose={() => {
          setRoleModalOpened(false);
          setEditingRole(null);
        }}
        onSubmit={handleRoleSubmit}
        existingRoles={roles}
        role={editingRole}
      />
    </>
  );
}

export default UploadAgreementModal;
